import { defineComponent, toRaw } from "vue";
import { Container } from "aurelia-dependency-injection";
import GoogleMaps = google.maps;
import moment from "moment";

import { getApplicationId } from "../../../../../common/vue-helpers/modifiers/application-modifiers";
import eventBus from "../../../../../utilities/eventBus";
import { getSVGIcon } from "../../../VisitMapSVGs";
import { adjustDate } from "../../../../../common/adjust-date";
import { ParentPermissionEnum } from "../../../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../../../enums/PermissionActionEnum";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { AnalyticsService } from "../../../../../services/analytics.service";
import { UserService } from "../../../../../services/user-service";
import { ToastrService } from "../../../../../services/toastr.service";
import { CalendarMapService } from "../../../../../services-vue/calendar-map-service";
import { IAddCalendarMarkerOptions } from "../../../../../resources-vue/vue-interfaces/i-calendar-map";
import {
    INearbyEligibleEmployeesPanelList,
    INearbyEligibleEmployeesPinsList,
    INearbyEmpPanelList,
    INearbyEmpPinsList,
    INearbyEmpPinsListRes
} from "../../../../../resources-vue/vue-interfaces/i-visit-map";
import { ITypeaheadOptions } from "../../../../../resources-vue/vue-interfaces/i-typeahead";
import { ApplicationEnum } from "../../../../../enums/ApplicationEnum";

import ReassignVisit from "../../../../../resources-vue/vue-dialogs/ReassignVisit/ReassignVisit.vue";
import MultiSelectFilter from "../../../../../resources-vue/vue-elements/MultiSelectFilter/MultiSelectFilter.vue";
import ProductPill from "../../../../../resources-vue/vue-elements/ProductPill/ProductPill.vue";

export default defineComponent({
    components: {
        MultiSelectFilter,
        ProductPill
    },

    props: {
        date: { type: String, default: moment().format("MM/DD/YYYY") },
        weekDayRange: { type: Number, default: 1 },
        showNearbyEmpPanel: { type: Boolean, default: true },
        selectedPatientVisit: { default: {} as any },
        patientInfoWindowContent: { type: String, default: "" },
        isUnassigned: { type: Function, default: null }
    },

    emits: ["update:showNearbyEmpPanel"],

    data() {
        return {
            userOs: "",
            _userService: null as UserService,
            _toastrService: null,
            _calendarMapService: null,
            _permissionManager: null,
            _analyticsService: null,
            _patientMarker: null as GoogleMaps.Marker,
            _bounds: null as GoogleMaps.LatLngBounds,
            _map: null as GoogleMaps.Map,
            isReassignAllowed: false as boolean,
            credentialsList: [] as string[],
            selectedCredentials: [] as ITypeaheadOptions[],
            selectedNearbyEmp: {} as INearbyEmpPinsListRes,
            nearbyEmpSearchTerm: "" as string,
            nearbyEmpDistance: 15 as number, // default to 15 miles
            startDate: null as Date,
            endDate: null as Date,
            nearbyEmpPinsList: [] as INearbyEmpPinsListRes[],
            nearbyEmpPanelList: [] as any,
            pageCount: 0 as number,
            totalPageCount: 1 as number,
            totalItemCount: 0 as number,
            observer: null,
            showLoading: true as boolean,
            dialogRef: null,
            timeout: null,
            getApplicationId
        };
    },

    created() {
        this.userOs = window.sessionStorage.getItem("userOs");
        this._userService = Container.instance.get(UserService);
        this._toastrService = Container.instance.get(ToastrService);
        this._calendarMapService = Container.instance.get(CalendarMapService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._analyticsService = Container.instance.get(AnalyticsService);
    },

    async mounted() {
        this.isReassignAllowed = this._permissionManager.checkPermission(
            ParentPermissionEnum.clinician,
            PermissionActionEnum.canReassign
        );
        this.credentialsList = await this._userService.getUserCredentials();

        this.observer = new IntersectionObserver(
            (entries: any) => {
                if (entries[0].isIntersecting) this.loadMoreEmployeesList();
            },
            { root: null, threshold: 0 }
        );
        this.observer.observe(this.$refs.trigger);

        this.getWeekRangeDates();
        this.markPatientLocation();
        await this.getNearbyEmpList();
    },

    watch: {
        nearbyEmpSearchTerm: async function () {
            await this.getNearbyEmpList();
        },
        nearbyEmpDistance: async function () {
            await this.getNearbyEmpList();
        },
        selectedCredentials: async function () {
            await this.getNearbyEmpList();
            // fix: watcher doesn't work for clear selected option
        }
    },

    methods: {
        getWeekRangeDates() {
            const currentDate = new Date(this.date);
            let dayRequested = this.weekDayRange - 1;
            if (dayRequested < 0) {
                dayRequested = 0;
            }
            let offset = dayRequested - currentDate.getDay();
            if (offset > 0) {
                offset -= 7;
            }
            this.startDate = adjustDate(currentDate, offset);
            this.endDate = adjustDate(this.startDate, 6);
        },

        userCredentialsFetch(filter: string) {
            return this.credentialsList
                .map((credential, index) => ({
                    name: credential,
                    value: index.toString()
                }))
                .filter((x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));
        },

        selectedItemsUpdated(options: any) {
            this.selectedCredentials = options;
        },

        async scheduledVisit(employeeId?: string) {
            let visitDetails = { ...this.selectedPatientVisit };
            visitDetails.TaskName = this.selectedPatientVisit.VisitName;
            visitDetails.UserFirstName = this.selectedPatientVisit.EmployeeName.FirstName;
            visitDetails.UserLastName = this.selectedPatientVisit.EmployeeName.LastName;
            visitDetails.FirstName = this.selectedPatientVisit.PatientName.FirstName;
            visitDetails.LastName = this.selectedPatientVisit.PatientName.LastName;

            this.dialogRef = this.$dialog.open(ReassignVisit, {
                props: { modal: true, showHeader: false },
                data: {
                    visitDetails,
                    selectedEmployee: employeeId
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        this.closeNearbyEmpPanel();
                    }
                }
            });
        },

        async getNearbyEmpList() {
            if (this.timeout) clearTimeout(this.timeout);

            this.timeout = setTimeout(async () => {
                this.pageCount = 0;
                this.totalPageCount = 1;
                this.totalItemCount = 0;
                this.showLoading = true;
                this.selectedNearbyEmp = {} as INearbyEmpPinsListRes;
                // remove previous markers
                if (this.nearbyEmpPinsList.length) {
                    const pins = [...this.nearbyEmpPinsList];
                    this.clearAllNearbyEmpMarkers(pins);
                }
                this.nearbyEmpPanelList = [];
                try {
                    if (
                        this.selectedPatientVisit?.Application === ApplicationEnum.AxxessHospiceFE ||
                        this.selectedPatientVisit?.Application === ApplicationEnum.AxxessPalliative
                    ) {
                        const pinsListPayload: INearbyEligibleEmployeesPinsList = {
                            Term: this.nearbyEmpSearchTerm,
                            Distance: Number(this.nearbyEmpDistance),
                            AgencyId: this.selectedPatientVisit.AgencyId,
                            Application: this.selectedPatientVisit.Application,
                            LocationId: this.selectedPatientVisit.LocationId,
                            Credentials: this.selectedCredentials?.map((credential) => `;${credential.name};`),
                            Latitude: this.selectedPatientVisit.PatientAddress.AddressLatitude,
                            Longitude: this.selectedPatientVisit.PatientAddress.AddressLongitude,
                            PatientId: this.selectedPatientVisit.PatientId,
                            TaskId: this.selectedPatientVisit.CustomTaskId
                        };
                        this.nearbyEmpPinsList =
                            await this._userService.getNearByEligibleEmployeesPinsList(pinsListPayload);
                    } else {
                        const pinsListPayload: INearbyEmpPinsList = {
                            Term: this.nearbyEmpSearchTerm,
                            Distance: Number(this.nearbyEmpDistance),
                            AgencyId: this.selectedPatientVisit.AgencyId,
                            Application: this.selectedPatientVisit.Application,
                            LocationId: this.selectedPatientVisit.LocationId,
                            Credentials: this.selectedCredentials?.map((credential) => `;${credential.name};`),
                            Latitude: this.selectedPatientVisit.PatientAddress.AddressLatitude,
                            Longitude: this.selectedPatientVisit.PatientAddress.AddressLongitude
                        };
                        this.nearbyEmpPinsList = await this._userService.getNearByEmpPinsList(pinsListPayload);
                    }
                    this.markNearbyEmpPins(this.nearbyEmpPinsList);
                } catch (error) {
                    this._toastrService.error({
                        title: "Error",
                        message: "There was a problem while getting Nearby Employees. Please try again."
                    });
                }
            }, 1200);
        },

        async loadMoreEmployeesList() {
            if (this.pageCount < this.totalPageCount) {
                try {
                    let response = null;
                    if (
                        this.selectedPatientVisit?.Application === ApplicationEnum.AxxessHospiceFE ||
                        this.selectedPatientVisit?.Application === ApplicationEnum.AxxessPalliative
                    ) {
                        const panelListPayload: INearbyEligibleEmployeesPanelList = {
                            Term: this.nearbyEmpSearchTerm,
                            Distance: Number(this.nearbyEmpDistance),
                            AgencyId: this.selectedPatientVisit.AgencyId,
                            Application: this.selectedPatientVisit.Application,
                            LocationId: this.selectedPatientVisit.LocationId,
                            Credentials: this.selectedCredentials?.map((credential) => `;${credential.name};`),
                            StartDate: moment(this.startDate).format("MM/DD/YYYY"),
                            EndDate: moment(this.endDate).format("MM/DD/YYYY"),
                            CurrentDate: this.date,
                            Latitude: this.selectedPatientVisit.PatientAddress.AddressLatitude,
                            Longitude: this.selectedPatientVisit.PatientAddress.AddressLongitude,
                            SelectedUser: this.selectedNearbyEmp.id,
                            Page: this.pageCount + 1,
                            PageLength: 50,
                            PatientId: this.selectedPatientVisit.PatientId,
                            TaskId: this.selectedPatientVisit.CustomTaskId
                        };
                        response = await this._userService.getNearByEligibleEmpPanelList(panelListPayload);
                    } else {
                        const panelListPayload: INearbyEmpPanelList = {
                            Term: this.nearbyEmpSearchTerm,
                            Distance: Number(this.nearbyEmpDistance),
                            AgencyId: this.selectedPatientVisit.AgencyId,
                            Application: this.selectedPatientVisit.Application,
                            LocationId: this.selectedPatientVisit.LocationId,
                            Credentials: this.selectedCredentials?.map((credential) => `;${credential.name};`),
                            StartDate: moment(this.startDate).format("MM/DD/YYYY"),
                            EndDate: moment(this.endDate).format("MM/DD/YYYY"),
                            CurrentDate: this.date,
                            Latitude: this.selectedPatientVisit.PatientAddress.AddressLatitude,
                            Longitude: this.selectedPatientVisit.PatientAddress.AddressLongitude,
                            SelectedUser: this.selectedNearbyEmp.id,
                            Page: this.pageCount + 1,
                            PageLength: 50
                        };
                        response = await this._userService.getNearByEmpPanelList(panelListPayload);
                    }
                    if (response) {
                        this.totalPageCount = response.PageCount;
                        this.totalItemCount += response.ItemCount;
                        this.pageCount++;
                        // Temporary fix: BE sends null if the selected user is not present in the filter criteria
                        const items = response.Items[0] === null ? response.Items.splice(1) : response.Items;
                        this.nearbyEmpPanelList = [...this.nearbyEmpPanelList, ...items];
                        this.showLoading = !(this.nearbyEmpPanelList.length === 0);
                    } else {
                        this._toastrService.error({
                            title: "Error",
                            message: "There was a problem while getting Nearby Employees. Please try again."
                        });
                    }
                } catch (error) {
                    this._toastrService.error({
                        title: "Error",
                        message: "There was a problem while getting Nearby Employees. Please try again."
                    });
                }
            } else {
                this.showLoading = false;
            }
        },

        markPatientLocation() {
            try {
                const lat: number = this.selectedPatientVisit.PatientAddress.AddressLatitude;
                const lon: number = this.selectedPatientVisit.PatientAddress.AddressLongitude;
                this._bounds = this._calendarMapService.getBounds();
                const image = require(`../../../../../assets/images/client-pin.svg`);
                this._map = this._calendarMapService.getMap(document.getElementById("NearbyEmp__VisitMap"), {
                    center: {
                        lat: lat,
                        lng: lon
                    },
                    zoom: 18, // Higher is zoom in and lower is zoom out
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                    gestureHandling: "greedy"
                });
                this._patientMarker = this._calendarMapService.addMarker({
                    position: {
                        lat: lat,
                        lng: lon
                    },
                    map: this._map,
                    icon: image
                });

                const info = this._calendarMapService.addInfoWindow({
                    content: this.patientInfoWindowContent
                });
                // Marker Events
                this._patientMarker.addListener("mouseover", () => {
                    info.open(this._map, this._patientMarker);
                });
                this._patientMarker.addListener("mouseout", () => {
                    info.close();
                });

                this._bounds.extend(this._patientMarker.getPosition());
            } catch (error) {
                console.error(error?.message);
            }
        },

        markNearbyEmpPins(employeeList: any, selectedId?: string) {
            for (let [index, emp] of employeeList.entries()) {
                const coords = {
                    lat: Number(emp.latitude),
                    lng: Number(emp.longitude)
                };
                let marker = null;
                const credentials = emp.credentials.split(",")[0];
                // used to grey out all the pins except the selected employee pin
                const isCurrentEmpSelected = selectedId === emp.id;
                const useGreyPinColor = selectedId ? !isCurrentEmpSelected : false;
                const icon = {
                    url:
                        "data:image/svg+xml;charset=UTF-8," +
                        encodeURIComponent(
                            useGreyPinColor ? getSVGIcon(credentials, "#707070") : getSVGIcon(credentials)
                        ),
                    scaledSize: isCurrentEmpSelected ? new google.maps.Size(55, 55) : new google.maps.Size(50, 50)
                };

                if (!!coords.lat && !!coords.lng) {
                    try {
                        const options: IAddCalendarMarkerOptions = {
                            position: coords,
                            map: this._map,
                            icon: icon
                        };
                        marker = this._calendarMapService.addMarker(options);
                        this._analyticsService.logEvent({
                            category: "Create-Marker",
                            action: "Coordinates"
                        });
                        employeeList[index].marker = marker;
                        this.initNearbyEmpMarkerEvent(employeeList[index]);
                    } catch (error) {
                        console.error(error);
                    }
                }
            }
        },

        initNearbyEmpMarkerEvent(employee: any) {
            if (employee.marker) {
                employee.marker.addListener("click", async () => {
                    // grey out rest of the pins
                    this.selectedNearbyEmp = employee;
                    this.clearAllNearbyEmpMarkers(this.nearbyEmpPinsList);
                    this.pageCount = 0;
                    this.totalPageCount = 1;
                    this.totalItemCount = 0;
                    this.nearbyEmpPanelList = [];
                    this.markNearbyEmpPins(this.nearbyEmpPinsList, this.selectedNearbyEmp.id);
                });
            }
        },

        highlightEmpPin(emp: any, empId: string) {
            this.clearAllNearbyEmpMarkers(this.nearbyEmpPinsList);
            this.markNearbyEmpPins(this.nearbyEmpPinsList, empId);
            this.selectedNearbyEmp = emp;
        },

        clearAllNearbyEmpMarkers(locations: any) {
            for (let [index, emp] of locations.entries()) {
                let currentMarker = locations[index].marker;
                if (currentMarker) {
                    toRaw(currentMarker).setMap(null);
                }
            }
        },

        async clearNearbyEmpFilters() {
            // reset all the filters
            this.nearbyEmpSearchTerm = "";
            this.nearbyEmpDistance = 15;
            (this.$refs.credentialsRef as any).toggleSelection();
            this.pageCount = 0;
            this.totalPageCount = 1;
            this.totalItemCount = 0;
            await this.getNearbyEmpList();
        },

        closeNearbyEmpPanel() {
            if (this.isUnassigned) {
                this.isUnassigned();
                return;
            }
            this.$emit("update:showNearbyEmpPanel", false);
            eventBus.emit("selectedVisitForNearbyEmp", {});
        }
    }
});
